/**
 * Model Manifest — the platform's CORE IP (ADR-002).
 *
 * One versioned JSON document per model is the single source of truth for:
 *  - backend routing (capabilities ∩ policy ∩ health ∩ cost)
 *  - pricing (Estimator/PriceBook) and entitlement caps
 *  - the schema-driven frontend form renderer (nothing model-specific is
 *    hardcoded in the UI — controls are generated from `ui.controls` +
 *    capability constraints)
 *  - MCP/CLI tool input schemas
 *  - the public status page (one component per active model)
 *
 * Adding a brand-new model — even with a novel control such as end-frame —
 * must require ONLY a manifest row (plus an adapter if the provider is new).
 */
import { z } from 'zod';
// ── Enums ────────────────────────────────────────────────────────────────────
export const MODALITIES = ['image', 'video', 'audio', 'llm', 'tool'];
export const GENERATION_MODES = ['t2i', 'i2i', 't2v', 'i2v', 't2a', 'v2v'];
export const LIFECYCLE_STATUSES = ['active', 'degraded', 'deprecated', 'disabled'];
/** Provider lanes: `mock` is the deterministic dev/CI lane (QA hard gate). */
export const PROVIDER_LANES = ['mock', 'fal', 'direct', 'selfhost'];
/**
 * Controls the frontend SchemaForm knows how to render. The renderer maps:
 * enum → Select, number+min/max → Slider, boolean → Toggle,
 * assetSlot → FrameUpload, refs → MultiImagePicker.
 */
export const UI_CONTROLS = [
    'prompt',
    'negativePrompt',
    'mode',
    'startFrame',
    'endFrame',
    'duration',
    'resolution',
    'aspect',
    'audioToggle',
    'referenceImages',
    'batch',
    'style',
    'voice',
    'text',
];
// ── Sub-schemas ──────────────────────────────────────────────────────────────
export const capabilitiesSchema = z
    .object({
    modes: z.array(z.enum(GENERATION_MODES)).min(1),
    startFrame: z.boolean().default(false),
    endFrame: z.boolean().default(false),
    /** Allowed clip durations in seconds (video/audio models). */
    durationsSec: z.array(z.number().positive()).optional(),
    resolutions: z.array(z.string()).optional(), // e.g. ["720p","1080p","4k"]
    aspects: z.array(z.string()).optional(), // e.g. ["16:9","9:16","1:1"]
    nativeAudio: z.boolean().default(false),
    /** Elements-style reference image conditioning. */
    referenceImages: z.object({ max: z.number().int().positive() }).optional(),
    /** Accepts an explicit inpainting mask (painted region). */
    mask: z.boolean().default(false),
    /** Accepts a driving-audio input (lipsync/talking avatars). */
    audioDrive: z.boolean().default(false),
    /** Max images per batch for image models. */
    maxBatch: z.number().int().positive().optional(),
    /** Free-form capability flags surfaced as chips (e.g. "motion_control"). */
    special: z.array(z.string()).default([]),
    /**
     * Arabic in-media text rendering grade — EVIDENCE-BASED (empirical
     * benchmark generations judged by an Arabic reader + published tests),
     * never vendor marketing alone. Drives the model-aware Arabic guard:
     * excellent/good surface a positive badge, fair a proofread hint,
     * poor/unknown a warning with verified alternatives.
     */
    arabicText: z.enum(['excellent', 'good', 'fair', 'poor', 'unknown']).default('unknown'),
})
    .strict();
/**
 * Prompting profile — the model's WIRE DIALECT + doc-sourced prompting
 * policy. Every field is verified against the provider's official
 * per-endpoint OpenAPI schema and the vendor's official prompting guide
 * (sources: docs/design/prompt-compiler.md). The PromptCompiler consumes
 * this to build each request; nothing endpoint-specific lives in code.
 */
export const promptingSchema = z
    .object({
    /** Provider param carrying the main text ('prompt' | 'text' vary per endpoint). */
    promptParam: z.string().default('prompt'),
    /**
     * Negative-prompt dialect: 'param' = dedicated negative param;
     * 'semantic' = folded into prose (Google/OpenAI/BFL style);
     * 'none' = never emit negatives (TTS/SFX read text verbatim).
     */
    negatives: z.enum(['param', 'semantic', 'none']).default('semantic'),
    negativeParam: z.string().default('negative_prompt'),
    /** Doc-sourced always-on negative terms (e.g. Kling's shipped default). */
    baseNegatives: z.array(z.string()).default([]),
    /** Provider's documented negative-prompt length cap (Wan/Qwen: 500). */
    negativeMaxChars: z.number().int().positive().default(500),
    /** Vendor's official quality booster appended to the prompt (Qwen's
     * published "positive magic": ", Ultra HD, 4K, cinematic composition."). */
    qualitySuffix: z.string().nullable().default(null),
    /** Aspect param name, or null when the endpoint has none. */
    aspectParam: z.string().nullable().default(null),
    /** Our aspect value → provider enum value (sent verbatim when absent). */
    aspectMap: z.record(z.string(), z.string()).default({}),
    /** Some providers accept aspect on t2v but not i2v (Kling). */
    aspectOnI2v: z.boolean().default(true),
    durationParam: z.string().nullable().default(null),
    /** 5 → 5 ('int') | "5" ('string') | "5s" ('suffixed') | 5000 ('ms'). */
    durationFormat: z.enum(['int', 'string', 'suffixed', 'ms']).default('int'),
    resolutionParam: z.string().nullable().default(null),
    /** Our resolution value → provider enum value (verbatim when absent). */
    resolutionMap: z.record(z.string(), z.string()).default({}),
    batchParam: z.string().nullable().default(null),
    /** Native-audio toggle param; always sent explicitly (provider defaults
     * to true on Veo/Kling and bills extra — silence must be intentional). */
    audioParam: z.string().nullable().default(null),
    /** Start-frame/input-image param ('image_url' | 'start_image_url'). */
    imageParam: z.string().default('image_url'),
    /** Input-video param on v2v/upscale endpoints ('video_url'). */
    videoParam: z.string().nullable().default(null),
    /** Edit endpoints take an ARRAY of images ('image_urls'): source first,
     * then references — set with imageParam pointing at the array param. */
    imageArray: z.boolean().default(false),
    /** Inpainting mask param ('mask_url'), null when unsupported. */
    maskParam: z.string().nullable().default(null),
    /** Driving-audio param ('audio_url'), null when unsupported. */
    audioUrlParam: z.string().nullable().default(null),
    endImageParam: z.string().nullable().default(null),
    referenceImagesParam: z.string().nullable().default(null),
    /** Endpoint takes ONE reference image (string), not an array (Wan edit). */
    referenceSingle: z.boolean().default(false),
    /** Voice param, dot-path capable ('voice' | 'voice_setting.voice_id'). */
    voiceParam: z.string().nullable().default(null),
    /** System-prompt param (Gemini image family): compiler directives go
     * here so the user's creative prompt stays untouched. */
    systemPromptParam: z.string().nullable().default(null),
    /**
     * Provider-side prompt rewriter/optimizer flag (Veo auto_fix, MiniMax
     * prompt_optimizer). The compiler forces it FALSE when exact in-media
     * text is requested — a silent rewrite may not alter quoted text.
     */
    rewriterParam: z.string().nullable().default(null),
    /** Doc-sourced params always sent verbatim (e.g. {output_format:'url'}). */
    extraDefaults: z.record(z.string(), z.unknown()).default({}),
    /** Extra params when content is Arabic (audio language boosts:
     * {language_code:'ar'} / {language_boost:'Arabic'}). */
    arabicExtras: z.record(z.string(), z.unknown()).default({}),
    /** Provider's documented prompt length cap (compiler budget). */
    maxPromptChars: z.number().int().positive().nullable().default(null),
})
    .strict();
/**
 * Credit pricing formula inputs. The estimator is a pure shared function
 * (see pricing.ts) so the FE can show LIVE totals with the exact same math
 * the ledger charges — `estimate == charge` is a QA hard gate.
 */
export const pricingSchema = z
    .object({
    formula: z.enum(['flat', 'base+perSecond', 'perImage', 'perChar', 'perMinute']),
    baseCredits: z.number().nonnegative().default(0),
    perSecondCredits: z.number().nonnegative().default(0),
    perImageCredits: z.number().nonnegative().default(0),
    perCharCredits: z.number().nonnegative().default(0),
    perMinuteCredits: z.number().nonnegative().default(0),
    resolutionMultipliers: z.record(z.string(), z.number().positive()).default({}),
    audioMultiplier: z.number().positive().default(1),
})
    .strict();
export const manifestSchema = z
    .object({
    /** Stable slug, e.g. "kling-3.0-pro". Never reused after sunset. */
    id: z.string().regex(/^[a-z0-9][a-z0-9.-]{1,63}$/),
    displayName: z.string().min(1),
    /** Vendor family used for grouping on pricing/status pages. */
    vendor: z.string().min(1),
    tagline: z.string().default(''),
    modality: z.enum(MODALITIES),
    provider: z.object({
        lane: z.enum(PROVIDER_LANES),
        adapter: z.string().min(1),
        /** Provider-side endpoint/model path, e.g. "fal-ai/kling-video/v3/pro". */
        endpoint: z.string().min(1),
        /**
         * Image-to-video endpoint when the provider separates it from t2v
         * (fal does). Used automatically when the spec carries a start frame;
         * absence + startFrame capability means the default endpoint accepts
         * image input directly.
         */
        i2vEndpoint: z.string().min(1).nullable().default(null),
    }),
    capabilities: capabilitiesSchema,
    /** Wire dialect + doc-sourced prompting policy (PromptCompiler input). */
    prompting: promptingSchema.default({}),
    pricing: pricingSchema,
    /** Rolling measured COGS — fed by invoices; Margin Guard input. */
    cogs: z
        .object({ usdPerSecond: z.number().nonnegative().default(0), source: z.string().default('estimate') })
        .default({ usdPerSecond: 0, source: 'estimate' }),
    /** Per-model caps applied when a plan grants unlimited (web surface only). */
    entitlementCaps: z
        .object({
        unlimited: z
            .object({ maxDurationSec: z.number().positive().optional(), maxResolution: z.string().optional() })
            .optional(),
    })
        .default({}),
    ui: z.object({
        controls: z.array(z.enum(UI_CONTROLS)).min(1),
        badge: z.enum(['TOP', 'NEW', 'TRENDING', 'PRO']).nullable().default(null),
        /** Storage key or URL of the hover-play thumb video; poster always exists. */
        thumbVideo: z.string().nullable().default(null),
        thumbPoster: z.string().nullable().default(null),
        /** Accent used for generated poster art (deterministic per model). */
        accent: z.string().default('#1D8387'),
        order: z.number().int().default(100),
    }),
    policy: z
        .object({
        regions: z.array(z.string()).default(['*']),
        moderationClass: z.enum(['standard', 'strict']).default('standard'),
    })
        .default({ regions: ['*'], moderationClass: 'standard' }),
    lifecycle: z
        .object({
        status: z.enum(LIFECYCLE_STATUSES).default('active'),
        sunsetAt: z.string().datetime().nullable().default(null),
        /** Auto-remap target for templates when this model is deprecated. */
        equivalentOf: z.string().nullable().default(null),
    })
        .default({ status: 'active', sunsetAt: null, equivalentOf: null }),
    version: z.string().min(1),
})
    .strict();
/**
 * Transform-tool tags: models that power a dedicated tool surface
 * (/tools/*) and must never appear in generation pickers.
 */
export const TOOL_TAGS = ['upscale', 'inpaint', 'videoedit', 'lipsync', 'swap'];
export function isToolModel(m) {
    return m.capabilities.special.some((s) => TOOL_TAGS.includes(s));
}
export function toPublicModel(m, healthScore) {
    return {
        id: m.id,
        displayName: m.displayName,
        vendor: m.vendor,
        tagline: m.tagline,
        modality: m.modality,
        capabilities: m.capabilities,
        pricing: m.pricing,
        entitlementCaps: m.entitlementCaps,
        ui: m.ui,
        lifecycle: m.lifecycle,
        healthScore,
        version: m.version,
    };
}
//# sourceMappingURL=manifest.js.map